import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { uk } from 'date-fns/locale';
import { TabIndices } from '@/common/i-doc/i-doc.registry.ts';
import { PageHint } from '@components/common/PageHint.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';
import { FinanceLegacyProfitFormDialog } from '@components/finance-legacy/FinanceLegacyProfitFormDialog.tsx';
import {
  financeLegacyService,
  FinanceLegacyProfit,
  FinanceLegacyOptions,
  SOURCE_APP_LEGACY,
  SOURCE_APP_WEB,
} from '@services/finance-legacy.service.ts';
import { toastService } from '@services/toast.service.ts';

// "profit" - суми, виведені на користь людини як прибуток. Окремий журнал
// від entries - без project_id/emp_id, тому фільтр лише по джерелу і періоду.
//
// Write-доступ (2026-08-21, по аналогії з "Витрати"/"Доходи") - "Додати"
// створює запис з sourceApp=WEB. Редагування/видалення - лише для власних
// (WEB) записів, легасі-виплати з нашого боку не чіпаються (бек кине 403).
const SourceAppChip: React.FC<{ sourceApp: string }> = ({ sourceApp }) => (
  sourceApp === SOURCE_APP_WEB
    ? <Chip label="Веб" color="primary" size="small" variant="outlined" />
    : <Chip label="Легасі" size="small" variant="outlined" />
);

export const FinanceLegacyProfitTab: React.FC<{ options: FinanceLegacyOptions }> = ({ options }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [sourceFilter, setSourceFilter] = useState('');
  const [sourceAppFilter, setSourceAppFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [items, setItems] = useState<FinanceLegacyProfit[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProfit, setEditingProfit] = useState<FinanceLegacyProfit | undefined>(undefined);
  const [deletingProfit, setDeletingProfit] = useState<FinanceLegacyProfit | undefined>(undefined);

  const fetchProfit = useCallback(async () => {
    setLoading(true);
    try {
      const result = await financeLegacyService.getProfit({
        from: fromDate ? fromDate.toISOString() : undefined,
        to: toDate ? toDate.toISOString() : undefined,
        sourceId: sourceFilter ? Number(sourceFilter) : undefined,
        sourceApp: sourceAppFilter || undefined,
        page,
        limit,
      });
      setItems(result.data);
      setTotal(result.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити виплати прибутку.');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, sourceFilter, sourceAppFilter, page, limit]);

  useEffect(() => { fetchProfit(); }, [fetchProfit]);

  const sourceLabel = (id: number | null) => options.sources.find((s) => s.value === id)?.label ?? id ?? '—';
  const currencyLabel = (id: number | null) => options.currencies.find((c) => c.value === id)?.label ?? id ?? '—';

  const openCreate = () => { setEditingProfit(undefined); setDialogOpen(true); };
  const openEdit = (profit: FinanceLegacyProfit) => { setEditingProfit(profit); setDialogOpen(true); };

  const confirmDelete = async () => {
    if (!deletingProfit) return;
    try {
      await financeLegacyService.deleteProfit(deletingProfit.id);
      toastService.success('Запис видалено.');
      fetchProfit();
    } catch {
      toastService.error('Не вдалося видалити запис.');
    } finally {
      setDeletingProfit(undefined);
    }
  };

  return (
    <Box data-i-doc={TabIndices.DEV_FINANCE_LEGACY_PROFIT}>
      <PageHint iDoc={TabIndices.DEV_FINANCE_LEGACY_PROFIT} />

      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
        <PageToolbar
          storageKey="filters:dev-finance-legacy-profit"
          filters={[
            {
              id: 'source',
              type: 'select',
              label: 'Джерело',
              value: sourceFilter,
              onChange: (v) => { setSourceFilter(v); setPage(1); },
              options: [{ value: '', label: 'Всі' }, ...options.sources.map((s) => ({ value: String(s.value), label: s.label }))],
            },
            {
              id: 'sourceApp',
              type: 'select',
              label: 'Інтерфейс',
              value: sourceAppFilter,
              onChange: (v) => { setSourceAppFilter(v); setPage(1); },
              options: [
                { value: '', label: 'Всі' },
                { value: SOURCE_APP_LEGACY, label: 'Легасі' },
                { value: SOURCE_APP_WEB, label: 'Веб' },
              ],
            },
            {
              id: 'from',
              type: 'custom',
              value: fromDate,
              renderCustom: () => (
                <DatePicker
                  label="Від"
                  value={fromDate}
                  onChange={(v) => { setFromDate(v); setPage(1); }}
                  slotProps={{ textField: { size: 'small' } }}
                />
              ),
            },
            {
              id: 'to',
              type: 'custom',
              value: toDate,
              renderCustom: () => (
                <DatePicker
                  label="До"
                  value={toDate}
                  onChange={(v) => { setToDate(v); setPage(1); }}
                  slotProps={{ textField: { size: 'small' } }}
                />
              ),
            },
          ]}
          onReset={() => { setFromDate(null); setToDate(null); setSourceFilter(''); setSourceAppFilter(''); setPage(1); }}
          showReset
          extraActions={<Button variant="contained" startIcon={<Add />} onClick={openCreate}>Додати</Button>}
        />
      </LocalizationProvider>

      {loading ? (
        <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Typography sx={{ mt: 4, textAlign: 'center' }}>Виплат не знайдено.</Typography>
      ) : (
        <TableContainer>
          <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                <TableCell>Дата</TableCell>
                {!isMobile && <TableCell>Джерело</TableCell>}
                <TableCell>Сума</TableCell>
                <TableCell>Сума (USD)</TableCell>
                {!isMobile && <TableCell>Інтерфейс</TableCell>}
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {item.createdAt ? new Date(item.createdAt).toLocaleDateString('uk-UA') : '—'}
                    {isMobile && (
                      <Box sx={{ mt: 0.5 }}>
                        <SourceAppChip sourceApp={item.sourceApp} />
                      </Box>
                    )}
                  </TableCell>
                  {!isMobile && <TableCell>{sourceLabel(item.sourceId)}</TableCell>}
                  <TableCell>{item.amount ?? '—'} {currencyLabel(item.currencyId)}</TableCell>
                  <TableCell>{item.amountUsd ?? '—'}</TableCell>
                  {!isMobile && <TableCell><SourceAppChip sourceApp={item.sourceApp} /></TableCell>}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {item.sourceApp === SOURCE_APP_WEB && (
                      <>
                        <IconButton size="small" onClick={() => openEdit(item)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => setDeletingProfit(item)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <UniversalPagination
        page={page}
        count={Math.ceil(total / limit)}
        limit={limit}
        total={total}
        onPageChange={setPage}
        onLimitChange={(v) => { setLimit(v); setPage(1); }}
        storageKey="pagination:dev-finance-legacy-profit"
      />

      <FinanceLegacyProfitFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        options={options}
        profit={editingProfit}
        onSaved={fetchProfit}
      />

      <ConfirmationDialog
        open={!!deletingProfit}
        onClose={() => setDeletingProfit(undefined)}
        onConfirm={confirmDelete}
        title="Видалити запис?"
        message="Запис буде позначено як видалений (soft-delete). Дію можна скасувати лише вручну в БД."
        confirmText="Видалити"
      />
    </Box>
  );
};
